import { useEffect, useMemo, useRef, useState, createContext, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { User, Session } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';
import { AppRole, Profile, UserRole } from '@/lib/supabase-types';
import {
  ACTIVE_WORKSPACE_KEY,
  LEGACY_ACTIVE_CLUB_KEY,
  pickActiveWorkspace,
  workspaceKey,
  workspacesFromProfiles,
  readViewAs,
  writeViewAs,
  clearViewAs,
  mergeViewAs,
  type Workspace,
  type WorkspaceKind,
} from '@/lib/workspaces';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  profiles: (Profile & { clubs: { name: string } | null })[];
  activeProfile: (Profile & { clubs: { name: string } | null }) | null;
  profile: (Profile & { clubs: { name: string } | null }) | null;
  userRoles: AppRole[];
  authReady: boolean;
  /** True once the profile/roles fetch reached a FINAL outcome (success or
   *  exhausted retries). Route guards must not read empty roles as "no
   *  permission" before this is true — that was the perceived-logout bug. */
  profileSettled: boolean;
  /** True when the fetch settled by failing: session intact but unusable. */
  profileError: boolean;
  isIdseatAdmin: boolean;
  isClubAdmin: boolean;
  clubId: string | null;
  clubName: string | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  switchClub: (clubId: string) => void;
  /** Every workspace (tenant, venue, operator) this person may open. */
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  /** Set when the active workspace is a kiosk (phase 2). */
  operatorId: string | null;
  /** The active venue, or the active tenant's home stadium. */
  stadiumId: string | null;
  switchWorkspace: (kind: WorkspaceKind, id: string) => void;
  /** The workspace an IDSeat admin opened without being a member ("Open portal"), when it is the active one. */
  viewingAs: Workspace | null;
  /** IDSeat only: open a tenant, venue or kiosk portal; rejects when the server refuses. */
  openAs: (kind: WorkspaceKind, id: string) => Promise<void>;
  /** Leave the viewed portal and return to the person's own workspaces. */
  closeViewAs: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // AuthProvider is rendered under PersistQueryClientProvider (see App.tsx), so
  // the React Query client is reachable here and can be cleared on sign-out.
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState<(Profile & { clubs: { name: string } | null })[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  // The one workspace IDSeat is viewing without a membership (spec 2026-10-05 §3.2).
  const [viewed, setViewed] = useState<Workspace | null>(null);
  // "kind:id" of the chosen workspace; the pre-workspace active_club_id is
  // read as a fallback so nobody lands in a different club after the change.
  const [activeKey, setActiveKey] = useState<string | null>(() => {
    try { return localStorage.getItem(ACTIVE_WORKSPACE_KEY); } catch { return null; }
  });
  const readLegacyClubId = () => {
    try { return localStorage.getItem(LEGACY_ACTIVE_CLUB_KEY); } catch { return null; }
  };
  const [userRoles, setUserRoles] = useState<AppRole[]>([]);
  const [authReady, setAuthReady] = useState(false);
  const [profileSettled, setProfileSettled] = useState(false);
  const [profileError, setProfileError] = useState(false);
  // Refs the mount-only effect and async closures can read without going
  // stale (the old isFetching state was permanently false inside them).
  const fetchingRef = useRef(false);
  const profileSettledRef = useRef(false);
  profileSettledRef.current = profileSettled;
  // Distinguishes a user-initiated logout from a server-side SIGNED_OUT
  // (expired/rotated refresh token) so only the latter gets explained.
  const manualSignOutRef = useRef(false);

  const allWorkspaces = useMemo(() => mergeViewAs(workspaces, viewed), [workspaces, viewed]);
  const activeWorkspace = pickActiveWorkspace(allWorkspaces, activeKey, readLegacyClubId());
  // "Viewing" only when the merged row is the active one AND is not a real membership.
  const viewingAs = viewed && activeWorkspace?.kind === viewed.kind && activeWorkspace.id === viewed.id
    && !workspaces.some((w) => w.kind === viewed.kind && w.id === viewed.id)
    ? viewed : null;
  const activeTenantId = activeWorkspace?.kind === 'tenant' ? activeWorkspace.id : null;
  // An IDSeat staff account carries a profile row per club PLUS a club-less
  // internal-admin row. With no saved choice, the default must be a real club:
  // on a fresh device profiles[0] was whichever row PostgREST returned first,
  // and when that was the club-less one the portal had no club at all (main, PR #151).
  const clubProfiles = profiles.filter(p => !!p.club_id);
  const activeProfile =
    clubProfiles.find(p => p.club_id === activeTenantId) || clubProfiles[0] || profiles[0] || null;
  const isIdseatAdmin = userRoles.includes('idseat_admin');
  // A membership-only user has no user_roles row but does have a tenant workspace.
  const isClubAdmin = userRoles.includes('club_admin') || allWorkspaces.some(w => w.kind === 'tenant');
  // clubId is the active TENANT. In a kiosk or venue workspace it is null, so a
  // club-keyed screen can never silently run against profiles[0]; the legacy
  // profile fallback applies only when no workspace exists at all.
  const clubId = activeTenantId ?? (activeWorkspace ? null : (activeProfile?.club_id ?? null));
  const clubName = activeWorkspace?.kind === 'tenant'
    ? activeWorkspace.name
    : (activeWorkspace ? null : (activeProfile?.clubs?.name || null));
  const operatorId = activeWorkspace?.kind === 'operator' ? activeWorkspace.id : null;
  const stadiumId = activeWorkspace?.kind === 'venue' ? activeWorkspace.id : (activeWorkspace?.stadium_id ?? null);

  const switchWorkspace = (kind: WorkspaceKind, id: string) => {
    const key = workspaceKey(kind, id);
    try { localStorage.setItem(ACTIVE_WORKSPACE_KEY, key); } catch { /* private mode: in-memory only */ }
    setActiveKey(key);
  };
  const switchClub = (id: string) => switchWorkspace('tenant', id);

  // One get_my_workspaces-shaped row for a portal IDSeat is not a member of.
  // Any refusal (not IDSeat, deleted kiosk) forgets the key: the person must
  // land in their real workspaces, never in an empty portal.
  const fetchViewAs = async (kind: WorkspaceKind, id: string): Promise<Workspace | null> => {
    const { data, error } = await supabase.rpc('get_workspace_as_idseat', { _kind: kind, _id: id });
    const row = (data as Workspace[] | null)?.[0] ?? null;
    if (error || !row) {
      if (error) console.warn('get_workspace_as_idseat refused:', error.message);
      clearViewAs();
      setViewed(null);
      return null;
    }
    return row;
  };

  const openAs = async (kind: WorkspaceKind, id: string) => {
    writeViewAs(kind, id);
    const row = await fetchViewAs(kind, id);
    if (!row) throw new Error('Could not open that portal');
    setViewed(row);
    switchWorkspace(kind, id);
  };

  const closeViewAs = () => {
    clearViewAs();
    setViewed(null);
    const first = workspaces[0];
    if (first) {
      switchWorkspace(first.kind, first.id);
    } else {
      try { localStorage.removeItem(ACTIVE_WORKSPACE_KEY); } catch { /* ignore */ }
      setActiveKey(null);
    }
  };

  // A transient network/API failure used to leave userRoles empty forever —
  // supabase-js resolves with an `error` FIELD rather than throwing, so the
  // old catch never even fired — and the route guard read that as "no
  // permission" (Access Denied for a logged-in admin). Now every failure is
  // retried, and the fetch always SETTLES: cleanly, or with profileError so
  // guards can route to a fresh sign-in instead.
  const PROFILE_RETRY_DELAYS_MS = [300, 800];

  const fetchProfile = async (userId: string) => {
    // A concurrent call must still release the render gate. Returning here
    // without it left authReady false forever, and ProtectedRoute renders a
    // spinner for exactly `!authReady && user`.
    if (fetchingRef.current) {
      setAuthReady(true);
      return;
    }
    fetchingRef.current = true;
    setProfileSettled(false);
    setProfileError(false);

    const attempt = async () => {
      const { data: profilesData, error: profilesErr } = await supabase
        .from('profiles')
        .select('*, clubs(name)')
        .eq('user_id', userId);
      if (profilesErr) throw profilesErr;

      const { data: rolesData, error: rolesErr } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);
      if (rolesErr) throw rolesErr;

      // Tolerant: an older database (function not deployed yet) or a transient
      // failure falls back to the profile rows below, never to "no workspace".
      // 'as any': the generated types predate this function (repo convention for new RPCs).
      const { data: wsData, error: wsErr } = await supabase.rpc('get_my_workspaces' as any);
      if (wsErr) console.warn('get_my_workspaces unavailable, deriving workspaces from profiles:', wsErr.message);

      return {
        profilesData: profilesData ?? [],
        rolesData: rolesData ?? [],
        wsData: wsErr ? null : ((wsData ?? []) as Workspace[]),
      };
    };

    try {
      for (let i = 0; ; i++) {
        try {
          const { profilesData, rolesData, wsData } = await attempt();
          setProfiles(profilesData as any);
          setUserRoles(rolesData.map(r => r.role as AppRole));
          const list = wsData ?? workspacesFromProfiles(profilesData as any);
          setWorkspaces(list);
          // A portal IDSeat was viewing stays open across reloads.
          const va = readViewAs();
          if (va) {
            const row = await fetchViewAs(va.kind, va.id);
            setViewed(row);
          }
          // One-time migration: a club chosen before workspaces existed stays chosen.
          const legacy = readLegacyClubId();
          let storedKey: string | null = null;
          try { storedKey = localStorage.getItem(ACTIVE_WORKSPACE_KEY); } catch { /* ignore */ }
          if (!storedKey && legacy && list.some(w => w.kind === 'tenant' && w.id === legacy)) {
            switchWorkspace('tenant', legacy);
          }
          setProfileSettled(true);
          setProfileError(false);
          return;
        } catch (error) {
          console.error(`Error fetching profile (attempt ${i + 1}):`, error);
          if (i >= PROFILE_RETRY_DELAYS_MS.length) {
            setProfileSettled(true);
            setProfileError(true);
            return;
          }
          // Release the render gate before sleeping — retries happen behind
          // a rendered app, not behind a spinner.
          setAuthReady(true);
          await new Promise(resolve => setTimeout(resolve, PROFILE_RETRY_DELAYS_MS[i]));
        }
      }
    } finally {
      fetchingRef.current = false;
      setAuthReady(true);
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  // Initial profile fetch removed here - will be handled by onAuthStateChange logic

  useEffect(() => {

    // 1. Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      // Tag errors with who hit them; anonymous fan pages stay unidentified.
      if (session?.user) setSentryUser({ id: session.user.id, email: session.user.email });

      // Drive the profile fetch from here rather than waiting for
      // onAuthStateChange.
      //
      // The morning deadlock (1 Sep 2026): overnight the access token expires.
      // getSession() still returns the stored session, so `user` is set, and
      // supabase-js then refreshes the token behind a Navigator lock. When that
      // refresh aborts on its 10s lock timeout no SIGNED_IN / TOKEN_REFRESHED /
      // INITIAL_SESSION event is ever emitted — so fetchProfile never ran,
      // authReady stayed false with `user` truthy, and every guarded route span
      // forever. fetchProfile is idempotent enough to also run from the
      // listener; whichever arrives first releases the gate.
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setAuthReady(true);
      }
    }).catch(err => {
      console.error("AuthProvider: Session fetch error:", err);
      setLoading(false);
      setAuthReady(true);
    });

    // 2. Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
          if (session?.user) {
            setSentryUser({ id: session.user.id, email: session.user.email });
            fetchProfile(session.user.id);
          }
        }

        if (event === 'SIGNED_OUT') {
          setProfiles([]);
          setUserRoles([]);
          setWorkspaces([]);
          setViewed(null);
          setProfileSettled(false);
          setProfileError(false);
          setAuthReady(true);
          clearSentryUser();
          // A server-side sign-out (expired or rotated refresh token) used to
          // dump people on /login with no explanation — say why.
          if (!manualSignOutRef.current) {
            toast.info('You were signed out — please sign in again.');
          }
          manualSignOutRef.current = false;
        }
      }
    );

    // Last resort. Anything that leaves authReady false — a hung getSession, a
    // refresh that neither resolves nor rejects — should degrade to a rendered
    // app the person can act on, not an indefinite spinner. Signed-out is the
    // safe failure: ProtectedRoute sends them to /login and signing in repairs
    // the session. Comfortably longer than supabase's own 10s lock timeout so
    // this only fires when that has already failed to produce an outcome.
    const gate = setTimeout(() => {
      setAuthReady((ready) => {
        if (!ready) console.warn('AuthProvider: auth never settled; rendering anyway');
        return true;
      });
      setLoading(false);
      // A profile fetch that never settled by now (hung network, no rejection)
      // is settled as errored, so route guards resolve to a fresh sign-in
      // instead of spinning. Harmless when signed out — guards check the user
      // first.
      if (!profileSettledRef.current) {
        setProfileSettled(true);
        setProfileError(true);
      }
    }, 15000);

    return () => {
      clearTimeout(gate);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // Pin the confirmation redirect to the login page on the current origin.
        // No admin-origin helper exists in @/lib/hostname (only fan-facing URL
        // helpers), so origin is the right base here — the admin portal serves
        // this flow from admin.idseat.com (or the dev/staging origin).
        emailRedirectTo: `${window.location.origin}/login`
      }
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    manualSignOutRef.current = true;
    // Local scope: log out THIS device only. The default 'global' scope
    // revokes every refresh token the user has — signing out on a phone was
    // silently killing the remembered desktop session.
    await supabase.auth.signOut({ scope: 'local' });
    setProfiles([]);
    setUserRoles([]);
    localStorage.removeItem(LEGACY_ACTIVE_CLUB_KEY);
    localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
    clearViewAs();
    setActiveKey(null);
    setWorkspaces([]);
    setViewed(null);
    // Logout hygiene: drop the persisted React Query cache (a prior user's
    // club data must not survive into the next session) and clear the
    // in-memory cache so nothing rehydrates from it.
    window.localStorage.removeItem('IDSEAT_QUERY_CACHE');
    queryClient.clear();
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      profiles,
      activeProfile,
      profile: activeProfile,
      userRoles,
      authReady,
      profileSettled,
      profileError,
      isIdseatAdmin,
      isClubAdmin,
      clubId,
      clubName,
      signIn,
      signUp,
      signOut,
      refreshProfile,
      switchClub,
      workspaces: allWorkspaces,
      activeWorkspace,
      operatorId,
      stadiumId,
      switchWorkspace,
      viewingAs,
      openAs,
      closeViewAs,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
